import { useState, useEffect, useRef } from 'react';
import NavBar from '../components/NavBar';
import { getDesignTokens } from '../utils/designTokens';
import { Icon } from '../utils/icons';
import { useThemeStore } from '../store/themeStore';
import { useFamilyStore } from '../store/familyStore';
import { 
  createMockFamily, 
  createMockEvents, 
  createMockTodos, 
  createMockTodoLists,
  createMockWeather 
} from '../utils/designSystemMocks';

// Import all components for examples
import UserMenu from '../components/UserMenu';
import ConfirmDialog from '../components/ConfirmDialog';
import ColorPicker from '../components/ColorPicker';
import ImageCropper from '../components/ImageCropper';
import MemberAvatar from '../components/MemberAvatar';
import EventCard from '../components/calendar/EventCard';
import EventPreview from '../components/calendar/EventPreview';
import MonthView from '../components/calendar/MonthView';
import WeekView from '../components/calendar/WeekView';
import DayView from '../components/calendar/DayView';
import TodayEvents from '../components/calendar/TodayEvents';
import ImportantDatesCountdown from '../components/calendar/ImportantDatesCountdown';
import CreateEventWizard from '../components/CreateEventWizard';
import TodoModal from '../components/todos/TodoModal';
import { EventTodoPanel } from '../components/todos/EventTodoPanel';
import WeatherWidget from '../components/WeatherWidget';
import { WeatherMap } from '../components/WeatherMap';
import BottomNav from '../components/mobile/BottomNav';
import BottomSheet from '../components/mobile/BottomSheet';
import MobileEventCard from '../components/mobile/MobileEventCard';
import MobileTodoCard from '../components/mobile/MobileTodoCard';
import MobileLayout from '../components/mobile/MobileLayout';
import MobileDayView from '../components/calendar/MobileDayView';
import MobileWeekView from '../components/calendar/MobileWeekView';
import { CalendarEvent } from '../types/calendar';
import { Todo } from '../types/todo';
import DatePicker from '../components/DatePicker';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToastStore } from '../store/toastStore';

export default function DesignSystem() {
  const { isDarkMode, toggleTheme } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { family: currentFamily } = useFamilyStore();
  const { showToast } = useToastStore();
  
  // Use mock family for examples (don't modify actual store)
  const mockFamily = currentFamily || createMockFamily();

  const mockEvents = createMockEvents();
  const mockTodos = createMockTodos();
  const mockLists = createMockTodoLists();
  const mockWeather = createMockWeather();

  // State for interactive examples
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#3b82f6');
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [showCreateEventWizard, setShowCreateEventWizard] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Ensure currentDate is valid and set to start of day to avoid timezone issues
  const [currentDate] = useState(() => {
    const date = new Date();
    date.setHours(12, 0, 0, 0); // Set to noon to avoid timezone edge cases
    return date;
  });
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(mockEvents[0]);

  // Scroll to section
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const categories = [
    { id: 'navigation', name: 'Navigation Components' },
    { id: 'calendar', name: 'Calendar Components' },
    { id: 'forms', name: 'Form & Input Components' },
    { id: 'modals', name: 'Modal & Dialog Components' },
    { id: 'todos', name: 'Todo Components' },
    { id: 'weather', name: 'Weather Components' },
    { id: 'layout', name: 'Layout Components' },
    { id: 'utility', name: 'Utility Components' },
    { id: 'mobile', name: 'Mobile-Specific Components' },
  ];

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} transition-colors duration-200`}>
      <NavBar className={`${tokens.card.bg} ${tokens.border.light} border-b shadow-sm`} containerClassName="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between w-full items-center">
          <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Design System</h1>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={toggleTheme}
              className={`${tokens.button.secondary} px-4 h-12 rounded-sm text-sm font-medium min-w-[100px] flex items-center justify-center gap-2 transition-all active:scale-95`}
            >
              <Icon 
                name={isDarkMode ? 'sun' : 'moon'} 
                size="md" 
                className={tokens.text.primary}
              />
              <span>{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
          </div>
        </div>
      </NavBar>

      <div className="flex max-w-7xl mx-auto">
        {/* Table of Contents - Sticky Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0 p-4">
          <div className={`${tokens.card.bg} ${tokens.card.border} border rounded-sm p-4 ${tokens.card.shadow} sticky top-4`}>
            <h3 className={`text-lg font-bold ${tokens.text.primary} mb-4`}>Components</h3>
            <nav className="space-y-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => scrollToSection(category.id)}
                  className={`block w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                    isDarkMode 
                      ? 'hover:bg-gray-800 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h2 className={`text-3xl font-bold ${tokens.text.primary} mb-4`}>
              Runow Design System
            </h2>
            <p className={`text-lg ${tokens.text.secondary} mb-2`}>
              Comprehensive component showcase with visual examples and props documentation
            </p>
            <p className={`text-sm ${tokens.text.muted}`}>
              All components are organized by category. Design tokens are integrated into each component section.
            </p>
          </div>

          {/* Navigation Components */}
          <section id="navigation" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Navigation Components</h3>
              
              {/* Design Tokens */}
              <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4 mb-6`}>
                <h4 className={`text-lg font-semibold ${tokens.text.secondary} mb-3`}>Design Tokens</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className={`font-semibold ${tokens.text.primary} mb-1`}>Backgrounds</div>
                    <div className={tokens.text.muted}>{tokens.bg.primary}</div>
                    <div className={tokens.text.muted}>{tokens.bg.panel}</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${tokens.text.primary} mb-1`}>Borders</div>
                    <div className={tokens.text.muted}>{tokens.border.light}</div>
                    <div className={tokens.text.muted}>{tokens.border.medium}</div>
                  </div>
                </div>
              </div>

              {/* NavBar */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>NavBar</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Top navigation bar with user menu, family context, and theme toggle
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <NavBar>
                    <span className={`${tokens.text.primary} font-semibold`}>Example Page Title</span>
                  </NavBar>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface NavBarProps {
  children: ReactNode;
  className?: string;
  isDarkMode?: boolean; // Deprecated
  containerClassName?: string;
}`}
                  </pre>
                </div>
              </div>

              {/* UserMenu */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>UserMenu</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Sliding menu panel with user info and account actions
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Toggle User Menu
                  </button>
                  <UserMenu 
                    isDarkMode={isDarkMode}
                    externalOpen={showUserMenu}
                    onExternalToggle={setShowUserMenu}
                  />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface UserMenuProps {
  isDarkMode?: boolean;
  isEmbedded?: boolean;
  hideButton?: boolean;
  externalOpen?: boolean;
  onExternalToggle?: (open: boolean) => void;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Calendar Components */}
          <section id="calendar" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Calendar Components</h3>
              
              {/* Design Tokens */}
              <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4 mb-6`}>
                <h4 className={`text-lg font-semibold ${tokens.text.secondary} mb-3`}>Design Tokens</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className={`font-semibold ${tokens.text.primary} mb-1`}>Event Colors</div>
                    <div className={tokens.text.muted}>Member-based gradients</div>
                  </div>
                  <div>
                    <div className={`font-semibold ${tokens.text.primary} mb-1`}>Borders</div>
                    <div className={tokens.text.muted}>{tokens.border.light}</div>
                  </div>
                </div>
              </div>

              {/* EventCard */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>EventCard</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Displays a calendar event with member info, time, and optional indicators
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light} space-y-3`}>
                  <EventCard event={mockEvents[0]} isDarkMode={isDarkMode} size="md" />
                  <EventCard event={mockEvents[3]} isDarkMode={isDarkMode} size="md" />
                  <EventCard event={mockEvents[2]} isDarkMode={isDarkMode} size="sm" />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface EventCardProps {
  event: CalendarEvent;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'header';
}`}
                  </pre>
                </div>
              </div>

              {/* EventPreview */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>EventPreview</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Modal preview showing full event details with edit/delete actions
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setSelectedEvent(mockEvents[0])}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Show Event Preview
                  </button>
                  {selectedEvent && (
                    <EventPreview
                      event={selectedEvent}
                      onEdit={() => {}}
                      onClose={() => setSelectedEvent(null)}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface EventPreviewProps {
  event: CalendarEvent;
  onEdit: () => void;
  onClose: () => void;
  onDelete?: () => void;
  isDarkMode?: boolean;
  isDeleting?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* MonthView */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>MonthView</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Full month calendar grid view with events
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <MonthView
                    year={currentDate.getFullYear()}
                    month={currentDate.getMonth()}
                    events={mockEvents}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface MonthViewProps {
  year: number;
  month: number;
  events: CalendarEvent[];
  onDateClick?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* WeekView */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>WeekView</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Weekly calendar view with time slots
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`} style={{ minHeight: '400px', position: 'relative', overflow: 'hidden' }}>
                  {currentDate && !isNaN(currentDate.getTime()) && (
                    <WeekView
                      date={currentDate}
                      events={mockEvents}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface WeekViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date, hour?: number) => void;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* DayView */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>DayView</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Single day calendar view with hourly time slots
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`} style={{ minHeight: '400px' }}>
                  <DayView
                    date={currentDate}
                    events={mockEvents}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface DayViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* TodayEvents */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>TodayEvents</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  List of today's events with collapsible functionality
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <TodayEvents events={mockEvents} isDarkMode={isDarkMode} />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface TodayEventsProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
  isEmbedded?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* ImportantDatesCountdown */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>ImportantDatesCountdown</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Displays upcoming important dates with countdown (requires events in store)
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <div className={`${tokens.text.muted} text-sm`}>
                    Note: This component requires events to be loaded in the event store. In the design system, it may not display if no important dates are available.
                  </div>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface ImportantDatesCountdownProps {
  days?: number;
  isDarkMode?: boolean;
  maxItems?: number;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Form & Input Components */}
          <section id="forms" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Form & Input Components</h3>
              
              {/* ColorPicker */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>ColorPicker</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Color selection component with 256-color grid and custom hex input
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <ColorPicker value={selectedColor} onChange={setSelectedColor} label="Select Color" />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  className?: string;
}`}
                  </pre>
                </div>
              </div>

              {/* ImageCropper */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>ImageCropper</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Circular image cropping tool with zoom and position controls
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setShowImageCropper(true)}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Open Image Cropper
                  </button>
                  {showImageCropper && (
                    <ImageCropper
                      image="https://via.placeholder.com/400x400"
                      onCrop={(url) => {
                        console.log('Cropped:', url);
                        setShowImageCropper(false);
                      }}
                      onCancel={() => setShowImageCropper(false)}
                      size={200}
                    />
                  )}
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface ImageCropperProps {
  image: string;
  onCrop: (croppedImageUrl: string) => void;
  onCancel: () => void;
  size?: number;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Modal & Dialog Components */}
          <section id="modals" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Modal & Dialog Components</h3>
              
              {/* ConfirmDialog */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>ConfirmDialog</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Confirmation dialog with customizable message and action buttons
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setShowConfirmDialog(true)}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Show Confirm Dialog
                  </button>
                  <ConfirmDialog
                    isOpen={showConfirmDialog}
                    title="Confirm Action"
                    message="Are you sure you want to proceed with this action?"
                    confirmText="Confirm"
                    cancelText="Cancel"
                    onConfirm={() => setShowConfirmDialog(false)}
                    onCancel={() => setShowConfirmDialog(false)}
                    isDestructive={false}
                  />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* CreateEventWizard */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>CreateEventWizard</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Multi-step wizard for creating and editing calendar events
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setShowCreateEventWizard(true)}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Open Create Event Wizard
                  </button>
                  {showCreateEventWizard && mockFamily && (
                    <CreateEventWizard
                      isOpen={showCreateEventWizard}
                      onClose={() => setShowCreateEventWizard(false)}
                      onSubmit={async () => {}}
                      family={mockFamily}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface CreateEventWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventData: EventFormData) => Promise<void>;
  editingEvent?: CalendarEvent | null;
  family: Family | null;
  isDarkMode: boolean;
  isProcessing?: boolean;
  initialDate?: Date | null;
}`}
                  </pre>
                </div>
              </div>

              {/* Date Picker */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>DatePicker</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  A compact date picker component that opens when clicking the current date or "Today" indicator. Shows badge counts for events (blue) and todos (orange) on each day. When a day has both events and todos, two badges appear side-by-side.
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setShowDatePicker(true)}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Open Date Picker
                  </button>
                  <DatePicker
                    isOpen={showDatePicker}
                    currentDate={currentDate}
                    onDateSelect={(date) => {
                      console.log('Date selected:', date);
                      setShowDatePicker(false);
                    }}
                    onClose={() => setShowDatePicker(false)}
                    events={mockEvents}
                    todos={mockTodos}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface DatePickerProps {
  isOpen: boolean;
  currentDate: Date;
  onDateSelect: (date: Date) => void;
  onClose: () => void;
  events: CalendarEvent[];
  todos: Todo[];
  isDarkMode?: boolean;
}`}
                  </pre>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2 mt-4`}>Features</h5>
                  <ul className={`text-xs ${tokens.text.tertiary} space-y-1 list-disc list-inside`}>
                    <li>Compact size (300px wide) to fit in a modal overlay</li>
                    <li>Shows badge counts for events (blue) and todos (orange)</li>
                    <li>When a day has both, two badges appear side-by-side</li>
                    <li>Current date (today) is highlighted with primary button color</li>
                    <li>Selected date (from main calendar) is highlighted with accent border</li>
                    <li>Month navigation arrows allow quick browsing</li>
                    <li>Clicking a date navigates the main calendar to that date and closes the picker</li>
                    <li>Touch-friendly with minimum 44px touch targets</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Todo Components */}
          <section id="todos" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Todo Components</h3>
              
              {/* TodoModal */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>TodoModal</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Modal for creating and editing todos with event linking
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setShowTodoModal(true)}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Open Todo Modal
                  </button>
                  {showTodoModal && mockFamily && (
                    <TodoModal
                      isOpen={showTodoModal}
                      onClose={() => setShowTodoModal(false)}
                      onSubmit={async () => {}}
                      lists={mockLists}
                    />
                  )}
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface TodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TodoFormData) => Promise<void>;
  editingTodo?: Todo | null;
  lists: Array<{ listId: string; name: string }>;
  isLoading?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* EventTodoPanel */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>EventTodoPanel</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Panel for managing todos linked to a specific event
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <div className={`${tokens.text.muted} text-sm mb-4`}>
                    Note: This component requires API access. In the design system, API calls are disabled to prevent errors.
                  </div>
                  <div className={`${tokens.card.bg} ${tokens.border.light} border rounded-sm p-4`}>
                    <div className="text-xs font-semibold mb-2">Event To Dos</div>
                    <div className="text-xs text-gray-500">Component would appear here with API access</div>
                  </div>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface EventTodoPanelProps {
  eventId: string;
  eventTitle: string;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Weather Components */}
          <section id="weather" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Weather Components</h3>
              
              {/* WeatherWidget */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>WeatherWidget</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Comprehensive weather display with current conditions, forecast, and maps
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <WeatherWidget isDarkMode={isDarkMode} location="family" variant="default" />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface WeatherWidgetProps {
  isDarkMode?: boolean;
  location?: string;
  variant?: 'default' | 'large' | 'compact';
}`}
                  </pre>
                </div>
              </div>

              {/* WeatherMap */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>WeatherMap</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Interactive map showing temperature or precipitation overlays
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <WeatherMap
                    latitude={37.7749}
                    longitude={-122.4194}
                    mapType="temperature"
                    isDarkMode={isDarkMode}
                    height={240}
                  />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface WeatherMapProps {
  latitude: number;
  longitude: number;
  mapType: 'temperature' | 'precipitation';
  isDarkMode?: boolean;
  height?: number;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Layout Components */}
          <section id="layout" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Layout Components</h3>
              
              {/* MobileLayout */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>MobileLayout</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Mobile layout wrapper with bottom navigation
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`} style={{ minHeight: '400px', position: 'relative' }}>
                  <MobileLayout>
                    <div className="p-4">
                      <p className={tokens.text.tertiary}>Mobile content area</p>
                    </div>
                  </MobileLayout>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface MobileLayoutProps {
  children: ReactNode;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Utility Components */}
          <section id="utility" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Utility Components</h3>
              
              {/* Toast */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>Toast</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Global notification system for success, error, info, and loading feedback
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => showToast('Saved successfully', 'success')}
                      className={`${tokens.button.primary} px-4 py-2`}
                    >
                      Show Success
                    </button>
                    <button
                      onClick={() => showToast('Something went wrong', 'error')}
                      className={`${tokens.button.secondary} px-4 py-2`}
                    >
                      Show Error
                    </button>
                    <button
                      onClick={() => showToast('Syncing updates', 'loading')}
                      className={`${tokens.button.secondary} px-4 py-2`}
                    >
                      Show Loading
                    </button>
                  </div>
                  <div className={`${tokens.text.muted} text-xs mt-3`}>
                    Toasts render globally via the ToastContainer in App.
                  </div>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`type ToastType = 'success' | 'error' | 'info' | 'loading';

showToast(message: string, type?: ToastType, duration?: number): string;`}
                  </pre>
                </div>
              </div>

              {/* LoadingSpinner */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>LoadingSpinner</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Reusable spinner for inline and button loading indicators
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <div className="flex items-center gap-4">
                    <LoadingSpinner size="sm" />
                    <LoadingSpinner size="md" />
                    <LoadingSpinner size="lg" />
                  </div>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  label?: string;
}`}
                  </pre>
                </div>
              </div>

              {/* MemberAvatar */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>MemberAvatar</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Displays member photo or initials with color background
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <div className="flex items-center gap-4">
                    {mockFamily?.members.slice(0, 4).map((member) => (
                      <MemberAvatar
                        key={member.userId}
                        member={{
                          memberId: member.userId,
                          memberName: member.name,
                          memberColor: member.color,
                          memberAvatar: member.avatar,
                        }}
                        size="lg"
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-4">
                    <MemberAvatar
                      member={{
                        memberId: '1',
                        memberName: 'Small',
                        memberColor: '#3b82f6',
                      }}
                      size="sm"
                    />
                    <MemberAvatar
                      member={{
                        memberId: '2',
                        memberName: 'Medium',
                        memberColor: '#ef4444',
                      }}
                      size="md"
                    />
                    <MemberAvatar
                      member={{
                        memberId: '3',
                        memberName: 'Large',
                        memberColor: '#10b981',
                      }}
                      size="lg"
                    />
                    <MemberAvatar
                      member={{
                        memberId: '4',
                        memberName: 'XLarge',
                        memberColor: '#8b5cf6',
                      }}
                      size="xl"
                    />
                  </div>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface MemberAvatarProps {
  member: {
    memberId?: string;
    memberName: string;
    memberColor?: string;
    memberAvatar?: string;
  };
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Mobile-Specific Components */}
          <section id="mobile" className="mb-12 scroll-mt-4">
            <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
              <h3 className={`text-2xl font-bold ${tokens.text.primary} mb-6`}>Mobile-Specific Components</h3>
              
              {/* BottomNav */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>BottomNav</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Bottom navigation bar with sliding indicator for mobile
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light} relative`} style={{ minHeight: '100px' }}>
                  <div className="pb-16">
                    <p className={tokens.text.tertiary}>Content area (BottomNav appears at bottom)</p>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0">
                    <BottomNav />
                  </div>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`// BottomNav has no props - uses React Router location`}
                  </pre>
                </div>
              </div>

              {/* BottomSheet */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>BottomSheet</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Slide-up bottom sheet modal for mobile interactions
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`}>
                  <button
                    onClick={() => setShowBottomSheet(true)}
                    className={`${tokens.button.secondary} px-4 py-2 mb-4`}
                  >
                    Show Bottom Sheet
                  </button>
                  <BottomSheet
                    isOpen={showBottomSheet}
                    onClose={() => setShowBottomSheet(false)}
                    title="Example Bottom Sheet"
                  >
                    <div className="p-4">
                      <p className={tokens.text.tertiary}>Bottom sheet content goes here</p>
                    </div>
                  </BottomSheet>
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  maxHeight?: string;
}`}
                  </pre>
                </div>
              </div>

              {/* MobileEventCard */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>MobileEventCard</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Touch-optimized event card for mobile views
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light} space-y-2`}>
                  <MobileEventCard event={mockEvents[0]} onClick={() => {}} />
                  <MobileEventCard event={mockEvents[2]} onClick={() => {}} />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface MobileEventCardProps {
  event: CalendarEvent;
  onClick: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}`}
                  </pre>
                </div>
              </div>

              {/* MobileTodoCard */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>MobileTodoCard</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Touch-optimized todo card for mobile views
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light} space-y-2`}>
                  {mockTodos.slice(0, 2).map((todo) => (
                    <MobileTodoCard
                      key={todo.todoId}
                      todo={todo}
                      onClick={() => {}}
                      familyMembers={mockFamily?.members}
                    />
                  ))}
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface MobileTodoCardProps {
  todo: Todo;
  onClick: () => void;
  onToggleStatus?: (todoId: string, status: TodoStatus) => void;
  familyMembers?: FamilyMember[];
}`}
                  </pre>
                </div>
              </div>

              {/* MobileDayView */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>MobileDayView</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Mobile-optimized day view calendar
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`} style={{ minHeight: '400px' }}>
                  <MobileDayView
                    date={currentDate}
                    events={mockEvents}
                  />
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface MobileDayViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
}`}
                  </pre>
                </div>
              </div>

              {/* MobileWeekView */}
              <div className="mb-8">
                <h4 className={`text-xl font-semibold ${tokens.text.secondary} mb-3`}>MobileWeekView</h4>
                <p className={`text-sm ${tokens.text.tertiary} mb-4`}>
                  Mobile-optimized week view calendar
                </p>
                <div className={`${tokens.bg.secondary} rounded-sm p-4 mb-4 border ${tokens.border.light}`} style={{ minHeight: '400px', position: 'relative', overflow: 'hidden' }}>
                  {currentDate && !isNaN(currentDate.getTime()) && (
                    <MobileWeekView
                      date={currentDate}
                      events={mockEvents}
                    />
                  )}
                </div>
                <div className={`${tokens.bg.primary} ${tokens.border.medium} border rounded-sm p-4`}>
                  <h5 className={`font-semibold ${tokens.text.primary} mb-2`}>Props</h5>
                  <pre className={`text-xs ${tokens.text.tertiary} overflow-x-auto`}>
{`interface MobileWeekViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date, hour?: number) => void;
  onEventClick?: (event: CalendarEvent) => void;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Footer Note */}
          <div className={`${tokens.card.bg} ${tokens.card.border} border-2 rounded-sm p-6 mb-8 ${tokens.card.shadow}`}>
            <h3 className={`text-xl font-bold ${tokens.text.primary} mb-4`}>Component Development Rules</h3>
            <div className={`space-y-2 ${tokens.text.secondary}`}>
              <p>
                <strong>Before using a component in a page:</strong> It MUST be documented in this design system page.
              </p>
              <p>
                <strong>If a component doesn't exist:</strong> It must be added to the design system first OR confirmed with the developer.
              </p>
              <p>
                <strong>New components:</strong> Should be added to the design system immediately after creation.
              </p>
              <p>
                <strong>Organization:</strong> Components should be organized by category in the design system.
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
